import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { Save, Shield, Settings, Info, Key, Phone, Building, Activity, AlertTriangle } from 'lucide-react';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Button } from '../../components/ui/Button';
import WhatsAppConsentPanel from './WhatsAppConsentPanel';
import WhatsAppAwayPanel from './WhatsAppAwayPanel';

const MetaSettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [connectionState, setConnectionState] = useState('checking'); // 'checking' | 'not_connected' | 'expired' | 'active'
  const [account, setAccount] = useState({
    accessToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    webhookVerifyToken: ''
  });
  // The masked display value from GET /meta/setup (e.g. "********1234") is shown only as a
  // placeholder, never loaded into account.accessToken — otherwise re-saving the form without
  // retyping the token would resubmit that masked string and clobber the real access token.
  const [accessTokenMask, setAccessTokenMask] = useState('');

  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    try {
      const res = await api.get('/meta/setup');
      if (res.data.success && res.data.account) {
        setAccessTokenMask(res.data.account.accessToken || '');
        setAccount({ ...res.data.account, accessToken: '' });
        setConnectionState(res.data.account.status === 'expired' ? 'expired' : 'active');
      } else {
        setConnectionState('not_connected');
      }
    } catch (err) {
      console.error('Failed to fetch Meta account', err);
      setConnectionState('not_connected');
    }
  };

  const handleChange = (e) => {
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Omit accessToken entirely when the field was left blank, so the backend keeps the
      // existing token instead of overwriting it with an empty value.
      const payload = { ...account };
      if (!payload.accessToken) delete payload.accessToken;
      const res = await api.post('/meta/setup', payload);
      if (res.data.success) {
        alertService.toast('success', 'Meta Account updated successfully');
        setConnectionState('active');
        // Re-fetch rather than use res.data.account directly — that response carries the
        // real (unmasked) access token, which must never land in client state/placeholders.
        await fetchAccount();
      }
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Failed to update account');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'API Connection', value: 'Live', icon: Activity, meta: 'Status' },
    { label: 'Phone ID', value: account.phoneNumberId || 'Not Set', icon: Phone },
    { label: 'WABA ID', value: account.businessAccountId || 'Not Set', icon: Building },
  ];

  return (
    <DashboardModuleShell
      title="Meta WhatsApp Configuration"
      subtitle="Connect your WhatsApp Business Cloud account and manage API credentials."
      icon={Settings}
      stats={stats}
    >
      {connectionState === 'not_connected' && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-[#FFF4E5] border border-[#FFB75D] rounded-md">
          <AlertTriangle className="w-4 h-4 text-[#DD7A01] shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-bold text-[#8C4B02]">WhatsApp is not connected yet</p>
            <p className="text-[12px] text-[#8C4B02] mt-0.5">
              No account is configured for this company — Direct Signal, Bulk Dispatch, and any AI/automation reply will fail until you fill in the credentials below and save.
            </p>
          </div>
        </div>
      )}
      {connectionState === 'expired' && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-[#FDE7E9] border border-[#F6A3AB] rounded-md">
          <AlertTriangle className="w-4 h-4 text-[#BA0517] shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-bold text-[#8E030F]">WhatsApp access token has expired</p>
            <p className="text-[12px] text-[#8E030F] mt-0.5">
              Meta rejected the last request with an authentication error, so this connection was automatically marked expired — every send will fail until you generate a fresh access token in Meta Developer Dashboard and paste it below.
            </p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <div className="sf-card">
            <div className="sf-card-header">
              <h3 className="sf-card-title flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#0176D3]" />
                Security &amp; Authentication
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="sf-card-body space-y-6">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-[#706E6B] block">
                  System Access Token
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#706E6B]" />
                  <input
                    type="password"
                    name="accessToken"
                    value={account.accessToken}
                    onChange={handleChange}
                    placeholder={accessTokenMask || "EAAW…"}
                    className="w-full pl-10 pr-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] text-[#080707] placeholder:text-[#706E6B] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none transition-all"
                    required={!accessTokenMask}
                  />
                </div>
                <p className="text-[11px] text-[#706E6B]">Permanent access token generated from your Meta Developer App. Must be a permanent System User token, not the 24-hour temporary token from Meta's quickstart panel — a temporary token expires and every send starts failing with an authentication error.</p>
                <p className="text-[11px] text-[#706E6B] bg-[#F4F6F9] border border-[#D8DDE6] rounded-md p-2">
                  Note: WhatsApp Flows use a separate "Flows API Token" field on the <a href="/dashboard/whatsapp-flows" className="text-[#0176D3] font-bold">WA Flows</a> page. If that token is set and valid, this CRM will now use it as a fallback for regular sends too — but keeping both fields set to the same valid permanent token avoids relying on the fallback.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] block">Phone Number ID</label>
                  <input
                    type="text"
                    name="phoneNumberId"
                    value={account.phoneNumberId}
                    onChange={handleChange}
                    placeholder="1092..."
                    className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] text-[#080707] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none transition-all"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] block">Business Account ID</label>
                  <input
                    type="text"
                    name="businessAccountId"
                    value={account.businessAccountId}
                    onChange={handleChange}
                    placeholder="1055..."
                    className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] text-[#080707] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-[#706E6B] block">Webhook Verify Token</label>
                <input
                  type="text"
                  name="webhookVerifyToken"
                  value={account.webhookVerifyToken}
                  onChange={handleChange}
                  placeholder="my_secret_token"
                  className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] text-[#080707] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none transition-all"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-[#706E6B] block flex items-center gap-2">
                  App Secret
                  {account.appSecretConfigured ? (
                    <span className="text-[10px] font-bold text-[#2E844A] normal-case">Verification enabled</span>
                  ) : (
                    <span className="text-[10px] font-bold text-[#DD7A01] normal-case">Verification disabled — add it to secure this connection</span>
                  )}
                </label>
                <input
                  type="password"
                  name="appSecret"
                  value={account.appSecret || ''}
                  onChange={handleChange}
                  placeholder={account.appSecretConfigured ? '••••••••••••••••' : 'From your Meta App > Settings > Basic'}
                  className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded-md text-[13px] text-[#080707] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none transition-all"
                />
                <p className="text-[11px] text-[#706E6B]">
                  Used to verify that incoming webhooks really come from Meta. Without it, anyone who knows your phone number ID could send forged events. Leave blank to keep the one already saved.
                </p>
              </div>

              <div className="pt-4 border-t border-[#D8DDE6] flex justify-end">
                <Button type="submit" loading={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Credentials
                </Button>
              </div>
            </form>
          </div>
        </div>

        <div className="space-y-6">
          <div className="sf-card">
            <div className="sf-card-header">
              <h3 className="sf-card-title flex items-center gap-2">
                <Info className="w-4 h-4 text-[#0176D3]" />
                Setup Guide
              </h3>
            </div>
            <div className="sf-card-body">
              <ul className="space-y-3 text-[12px] text-[#706E6B]">
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[10px] shrink-0 font-bold text-[#080707]">1</span>
                  Navigate to your Meta App Dashboard
                </li>
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[10px] shrink-0 font-bold text-[#080707]">2</span>
                  Add the "WhatsApp" product to your app
                </li>
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[10px] shrink-0 font-bold text-[#080707]">3</span>
                  Copy credentials into the fields provided
                </li>
              </ul>
            </div>
          </div>

          <div className="sf-card">
            <div className="sf-card-header">
              <h3 className="sf-card-title">Callback URL</h3>
            </div>
            <div className="sf-card-body">
              <div className="p-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded-md break-all">
                <code className="text-[12px] font-bold text-[#0176D3]">
                  {window.location.origin}/api/meta/webhook
                </code>
              </div>
              <p className="text-[11px] text-[#706E6B] mt-3">
                Configure this URL in your Meta Webhook settings to receive real-time updates.
              </p>
            </div>
          </div>

          <WhatsAppConsentPanel />
          <WhatsAppAwayPanel />
        </div>
      </div>
    </DashboardModuleShell>
  );
};

export default MetaSettingsPage;
